import { Box, Dialog, DialogContent, DialogTitle, Typography, Autocomplete, TextField, IconButton, LinearProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Close as CloseIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../../shared/components/MUI/button/Button';
import { businessEmployeeService, clientPackageService, clientService, feedback, IClientView, IServicePackageView } from '../../../shared/services';
import { PAYMENT_METHODS } from '../../../shared/constants/paymentMethods';
import { CreateClientModal } from '../../../shared/components/SconApp/createClientModal/CreateClientModal';

interface SellPackageModalProps {
  open: boolean;
  onClose: () => void;
  pkg: IServicePackageView;
  businessId: number;
}

export const SellPackageModal: React.FC<SellPackageModalProps> = ({ open, onClose, pkg, businessId }) => {
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<IClientView | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(undefined);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: clients, isFetching: isFetchingClients } = useQuery({
    queryKey: ['clients', businessId, search],
    queryFn: () => clientService.searchClients(businessId, search || undefined),
    enabled: open,
  });

  const { data: employees } = useQuery({
    queryKey: ['businessEmployees', businessId],
    queryFn: () => businessEmployeeService.get(businessId, true),
    enabled: open,
  });

  const { mutate: sellPackage, isLoading } = useMutation(
    () => {
      const uid = selectedClient?.userId;
      if (!uid) throw new Error('Cliente sem userId');
      return clientPackageService.sell({
        servicePackageId: pkg.id,
        userId: uid,
        businessId,
        paymentMethod: paymentMethod || undefined,
        sellerBusinessEmployeeId: selectedEmployeeId,
      });
    },
    {
      onSuccess: () => {
        feedback('Pacote vendido com sucesso!', 'success');
        queryClient.invalidateQueries({ queryKey: ['clientPackages'] });
        onClose();
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  const handleSell = () => {
    if (!selectedClient?.userId) {
      feedback('Selecione um cliente com cadastro (usuário)', 'error');
      return;
    }
    sellPackage();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='h6'>Vender pacote</Typography>
        <IconButton onClick={onClose} size='small'><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2} mt={1}>
          <Box p={2} bgcolor='action.hover' borderRadius={1}>
            <Typography variant='subtitle1' fontWeight={600}>{pkg.name}</Typography>
            <Typography variant='body2' color='text.secondary'>
              {pkg.totalUses} usos — R$ {pkg.amount.toFixed(2)}
            </Typography>
            <Typography variant='caption'>• {pkg.serviceName}</Typography>
          </Box>

          <Box display='flex' gap={1} alignItems='flex-start'>
            <Autocomplete
              sx={{ flex: 1 }}
              options={clients || []}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.name}${opt.phone ? ` — ${opt.phone}` : ''}`}
              value={null}
              onChange={(_, value) => {
                if (value && typeof value !== 'string') {
                  setSelectedClient(value);
                } else {
                  setSelectedClient(null);
                }
              }}
              onInputChange={(_, value) => setSearch(value)}
              loading={isFetchingClients}
              loadingText='Buscando clientes...'
              noOptionsText='Nenhum cliente encontrado'
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Cliente'
                  variant='outlined'
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isFetchingClients ? <LinearProgress sx={{ width: 20 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <IconButton
              onClick={() => setClientModalOpen(true)}
              color='primary'
              title='Novo cliente'
              sx={{ mt: 0.5 }}
            >
              <PersonAddIcon />
            </IconButton>
          </Box>

          {selectedClient && (
            <Typography variant='body2' color='primary'>
              ✓ {selectedClient.name} {selectedClient.phone ? `(${selectedClient.phone})` : ''}
            </Typography>
          )}

          {/* Professional selector — only when package has commission */}
          {pkg.commissionPercent != null && pkg.commissionPercent > 0 && (
            <FormControl fullWidth size='small'>
              <InputLabel>Profissional (comissão)</InputLabel>
              <Select
                value={selectedEmployeeId ?? ''}
                label='Profissional (comissão)'
                onChange={(e) => setSelectedEmployeeId(e.target.value ? Number(e.target.value) : undefined)}
              >
                <MenuItem value=''>
                  <em>Selecionar</em>
                </MenuItem>
                {employees?.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth size='small'>
            <InputLabel>Forma de pagamento</InputLabel>
            <Select
              value={paymentMethod}
              label='Forma de pagamento'
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <MenuItem value=''>
                <em>Selecionar</em>
              </MenuItem>
              {PAYMENT_METHODS.map((method) => (
                <MenuItem key={method} value={method}>
                  {method}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display='flex' justifyContent='flex-end' gap={1}>
            <Button label='Cancelar' variant='outlined' onClick={onClose} />
            <Button
              label='Confirmar venda'
              variant='contained'
              onClick={handleSell}
              isLoading={isLoading}
              disabled={!selectedClient}
            />
          </Box>
        </Box>
      </DialogContent>

      <CreateClientModal
        open={clientModalOpen}
        businessId={businessId}
        onClose={() => setClientModalOpen(false)}
      />
    </Dialog>
  );
};
